import { connect } from 'react-redux';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import React from 'react';

import { SearchBar } from '@commons/utils/styledLibraryComponents';
import DisplayNumber from '@commons/DisplayNumber';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { HeaderContainer, Metric, Metrics } from './styledComponents';

const SEARCH_BAR_WIDTH = 320;

/**
 * Header of an order table: the product search on the left, the metrics of the order on the right.
 * Whatever a status adds in between — the summary toggle of a draft — comes in as children.
 *
 * From `En préparation` onwards the order carries a BDL total besides the ordered one: handing it
 * over renders both totals, each spelling out the document it sums.
 *
 * The line count and the totals describe the whole order, never what the search leaves on screen.
 */
export const OrderTableHeader = (props) => {
  const {
    search,
    onSearchChange,
    lineCount,
    totalWithTaxes,
    preparedTotalWithTaxes,
    children,
    // From Redux
    currency,
  } = props;

  const hasPreparedTotal = preparedTotalWithTaxes !== null;

  const currencyCode = currency?.alphabeticCode ?? '';

  const renderMetric = (label, amount) => (
    <Metric>
      <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
        {label}
      </Text>
      <DisplayNumber font={ENUM_FONTS.TEXT_BIG_HEIGHT_16} number={amount} />
    </Metric>
  );

  return (
    <HeaderContainer>
      <SearchBar
        maxWidth={SEARCH_BAR_WIDTH}
        minWidth={SEARCH_BAR_WIDTH}
        placeholder={i18next.t('EXTERNAL_ORDERS.FORM.SEARCH_PRODUCT_PLACEHOLDER')}
        setValue={onSearchChange}
        value={search}
      />
      {children}
      <Metrics>
        <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_BIG_HEIGHT_16}>
          {i18next.t('EXTERNAL_ORDERS.FORM.LINE_COUNT', { lineCount })}
        </Text>
        {renderMetric(
          i18next.t(
            hasPreparedTotal
              ? 'EXTERNAL_ORDERS.FORM.TOTAL_WITH_TAXES_BDC'
              : 'EXTERNAL_ORDERS.FORM.TOTAL_WITH_TAXES',
            { currency: currencyCode },
          ),
          totalWithTaxes,
        )}
        {hasPreparedTotal &&
          renderMetric(
            i18next.t('EXTERNAL_ORDERS.FORM.TOTAL_WITH_TAXES_BDL', { currency: currencyCode }),
            preparedTotalWithTaxes,
          )}
      </Metrics>
    </HeaderContainer>
  );
};

OrderTableHeader.propTypes = {
  search: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  lineCount: PropTypes.number.isRequired,
  totalWithTaxes: PropTypes.number.isRequired,
  preparedTotalWithTaxes: PropTypes.number,
  children: PropTypes.node,
};

OrderTableHeader.defaultProps = {
  preparedTotalWithTaxes: null,
  children: null,
};

const mapStateToProps = (state) => ({
  currency: state.baseReducer.currency,
});

export default connect(mapStateToProps)(OrderTableHeader);
